import { formatParisDateTime } from '@flux/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Checkbox } from '@/components/ui/checkbox.js';
import { ConfirmDialog } from '@/components/ui/confirm-dialog.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.js';
import { useMaintenanceState } from '@/features/settings/use-maintenance-state.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { toast } from '@/lib/toast.js';
import { flushFileQueue, toggleMaintenance } from './api-martelet.js';
import { SectionTable, SettingsSectionSkeleton } from './settings-parts.js';

const SECTION_TITLE = 'Suspension des envois';
const SECTION_DESCRIPTION =
  'Pendant une suspension, les fichiers continuent d’être générés mais ne partent pas chez Martelet : ils s’accumulent dans une file, envoyée à la reprise.';

/**
 * « Suspension des envois » (`02` §12.3), ex-onglet Maintenance : le mot
 * « maintenance » ne disait pas ce qui se passe. Suspendre, c'est arrêter
 * d'envoyer les CSV à Martelet — les fichiers continuent d'être générés et
 * s'empilent dans une file, que l'on vide en reprenant, ou à la demande.
 *
 * Réutilise `useMaintenanceState()` (aussi lu par le bandeau global) : toute
 * mutation invalide `queryKeys.settings.maintenance()` pour que le bandeau se
 * mette à jour immédiatement.
 */
export function MarteletSuspensionSection(): JSX.Element {
  const queryClient = useQueryClient();
  const query = useMaintenanceState();
  const [reason, setReason] = useState('');
  const [flushQueue, setFlushQueue] = useState(false);
  const [enableDialogOpen, setEnableDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [flushDialogOpen, setFlushDialogOpen] = useState(false);

  const flushMutation = useMutation({
    mutationFn: flushFileQueue,
    onSuccess: (result) => {
      toast.success(result.testMode ? 'Mode test actif : les fichiers restent en attente.' : `${String(result.queuedCount)} fichier(s) remis en file d’envoi.`);
      void queryClient.invalidateQueries({ queryKey: queryKeys.settings.maintenance() });
      setFlushDialogOpen(false);
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  const mutation = useMutation({
    mutationFn: toggleMaintenance,
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.settings.maintenance(), data);
      void queryClient.invalidateQueries({ queryKey: queryKeys.settings.maintenance() });
      toast.success(data.enabled ? 'Envois suspendus.' : 'Envois repris.');
      setReason('');
      setFlushQueue(false);
      setEnableDialogOpen(false);
      setDisableDialogOpen(false);
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  if (query.isPending) {
    return <SettingsSectionSkeleton fields={2} />;
  }
  if (query.isError || !query.data) {
    return (
      <PageSection title={SECTION_TITLE}>
        <ErrorState
          message={
            query.error instanceof ApiError
              ? query.error.message
              : 'Une erreur technique est survenue.'
          }
          detail={query.error instanceof ApiError ? query.error.detail : undefined}
          onRetry={() => {
            void query.refetch();
          }}
          className="border-0 bg-transparent py-6"
        />
      </PageSection>
    );
  }

  const state = query.data;

  return (
    <>
      <PageSection title={SECTION_TITLE} description={SECTION_DESCRIPTION}>
        <div className="flex flex-col gap-5">
          {state.enabled ? (
            <div className="flex flex-col gap-1 rounded-md border border-status-red-border bg-status-red-bg px-3 py-2.5 text-sm text-status-red-fg">
              <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
                <span>État :</span>
                <span className="font-semibold">Envois suspendus</span>
                {state.since ? (
                  <span className="tabular-nums">
                    depuis le {formatParisDateTime(new Date(state.since))}
                  </span>
                ) : null}
              </p>
              {state.reason ? <p className="pl-6">Motif : {state.reason}</p> : null}
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium">État :</span>
              <Badge tone="green">Envois actifs</Badge>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <p className="text-sm tabular-nums text-muted-foreground">
              {state.queuedCount} fichier(s) en attente d’envoi.
            </p>
            {state.queuedCount > 0 ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setFlushDialogOpen(true);
                }}
              >
                Envoyer maintenant
              </Button>
            ) : null}
          </div>

          {state.enabled ? (
            <div>
              <Button
                type="button"
                onClick={() => {
                  setDisableDialogOpen(true);
                }}
              >
                Reprendre les envois
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-end gap-2 border-t border-border pt-5">
              <Field
                label="Motif"
                htmlFor="maintenance-reason"
                className="w-full sm:w-auto sm:flex-1"
              >
                <Input
                  id="maintenance-reason"
                  value={reason}
                  onChange={(event) => {
                    setReason(event.target.value);
                  }}
                  placeholder="Incident Martelet, opération planifiée…"
                />
              </Field>
              <Button
                type="button"
                variant="outline"
                disabled={reason.trim() === ''}
                onClick={() => {
                  setEnableDialogOpen(true);
                }}
              >
                Suspendre les envois
              </Button>
            </div>
          )}

          <div className="flex flex-col gap-3 border-t border-border pt-5">
            <h3 className="text-sm font-semibold">Historique des suspensions</h3>
            {state.history.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucune suspension enregistrée.</p>
            ) : (
              <SectionTable atEnd>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Motif</TableHead>
                      <TableHead>Par</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {state.history.map((entry, index) => (
                      <TableRow key={`${entry.at}-${entry.enabled ? '1' : '0'}-${index}`}>
                        <TableCell className="whitespace-nowrap tabular-nums">
                          {formatParisDateTime(new Date(entry.at))}
                        </TableCell>
                        <TableCell>
                          <Badge tone={entry.enabled ? 'red' : 'green'}>
                            {entry.enabled ? 'Suspendus' : 'Repris'}
                          </Badge>
                        </TableCell>
                        <TableCell>{entry.reason ?? '—'}</TableCell>
                        <TableCell>{entry.byName ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </SectionTable>
            )}
          </div>
        </div>
      </PageSection>

      <ConfirmDialog
        open={enableDialogOpen}
        onOpenChange={setEnableDialogOpen}
        title="Suspendre les envois vers Martelet ?"
        description="Les fichiers continueront d’être générés mais resteront en file d’attente jusqu’à la reprise."
        confirmLabel="Suspendre"
        isConfirming={mutation.isPending}
        onConfirm={() => {
          mutation.mutate({ enabled: true, reason: reason.trim(), flushQueue: false });
        }}
      />

      <ConfirmDialog
        open={disableDialogOpen}
        onOpenChange={setDisableDialogOpen}
        title="Reprendre les envois vers Martelet ?"
        description={
          <div className="flex flex-col gap-3">
            <p>
              {state.queuedCount > 0
                ? `${state.queuedCount} fichier(s) sont en attente d’envoi.`
                : 'Aucun fichier n’est en attente d’envoi.'}
            </p>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <Checkbox
                checked={flushQueue}
                onCheckedChange={(checked) => {
                  setFlushQueue(checked === true);
                }}
              />
              Envoyer immédiatement les fichiers en file
            </label>
          </div>
        }
        confirmLabel="Reprendre"
        isConfirming={mutation.isPending}
        onConfirm={() => {
          mutation.mutate({ enabled: false, flushQueue });
        }}
      />

      <ConfirmDialog
        open={flushDialogOpen}
        onOpenChange={setFlushDialogOpen}
        title="Envoyer les fichiers en attente ?"
        description={`${String(state.queuedCount)} fichier(s) partiront immédiatement chez Martelet, sans lever la suspension.`}
        confirmLabel="Envoyer"
        isConfirming={flushMutation.isPending}
        onConfirm={() => {
          flushMutation.mutate();
        }}
      />
    </>
  );
}
